import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router";
import { v4 as uuidv4 } from "uuid";
import BotEditor from "@nualang/nualang-ui-components/dist/Editors/Bot";
import {
  bots as botQuerys,
  avatars as avatarQuerys,
  courses as courseQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import { getCourseSectionTopic } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopics";
import { getCourseSectionTopicPhraseLists } from "@nualang/nualang-api-and-queries/APIs/CourseSectionTopicPhraseLists";
import { getCourse } from "@nualang/nualang-api-and-queries/APIs/Courses";
import useServices from "../../../hooks/useServices";
import useAudioPlayer from "../../../hooks/useAudioPlayer";
import config from "../../../config";
import { messageBot } from "@nualang/nualang-api-and-queries/APIs/Bots";
import {
  UploadBotImage,
  UploadBotExerciseImage,
} from "../../../utils/imageUpload";
import {
  highlightSpeechText,
  stopHighlightingSpeechText,
  objToSearchString,
  searchStringToObj,
} from "../../../utils/index";
import {
  getBot,
  updateBot as updateBotAPI,
} from "@nualang/nualang-api-and-queries/APIs/Bots";
import {
  avatars as getAvatars,
  makeChatGptApiRequest,
} from "@nualang/nualang-api-and-queries/APIs/Services";
import {
  bots as botHelpers,
  courses as courseHelpers,
} from "../../../utils/helpers";
import { useQueryClient } from "@tanstack/react-query";
import useConfirm from "@nualang/nualang-ui-components/dist/hooks/useConfirm";
import { difficulties, getForLangCharacters } from "../../../utils/index";

import logger from "../../../logger";
import { useApp, useSubscription } from "../../../hooks";

function EditBot(props) {
  const { t, i18n } = useTranslation();
  const [tts, translate] = useServices();

  const {
    authData: { username, attributes },
    navigate,
    verificationStatus,
  } = props;
  const { addToQueue, clearQueue, stop } = useAudioPlayer();
  const params = useParams();
  const { classroomId, courseId, sectionId, topicId, botId } = params;
  const location = useLocation();
  const {
    openSnackbar,
    handleShareBot,
    handleOpenModerateDialog,
  } = useApp();

  const { subscription } = useSubscription();
  const queryClient = useQueryClient();

  const [isBotEditorOpen, setIsBotEditorOpen] = useState(true);
  const [timeoutArray, setTimeoutArray] = useState([]);
  const [confirm] = useConfirm(t);

  const isUserInternal = config.internalDomains.some((domain) =>
    (attributes.email || "").endsWith(domain),
  );

  const courseQuery = courseQuerys.useCourse(
    async (cId) => {
      const course = await getCourse(cId);
      const formattedCourse = await courseHelpers.formatCourse(
        course.Item,
        props.authData,
      );
      return formattedCourse;
    },
    {
      courseId,
      username: props.authData.username,
    },
    { enabled: !!courseId },
  );

  const course =
    courseQuery.isSuccess && courseQuery.data && courseQuery.data.courseId
      ? courseQuery.data
      : {};

  const { forLang = "" } = course;

  const handleCloseBotEditor = () => {
    setIsBotEditorOpen(false);
    if (classroomId && courseId && sectionId && topicId) {
      navigate(
        `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}`,
      );
    } else if (courseId && sectionId && topicId) {
      navigate(`/courses/${courseId}/${sectionId}/${topicId}`);
    } else {
      navigate("/bots");
    }
  };

  const avatarsQuery = avatarQuerys.useAvatars(async () => {
    const avatars = await getAvatars();
    return avatars;
  });

  const avatars =
    avatarsQuery.isSuccess &&
    Array.isArray(avatarsQuery.data) &&
    avatarsQuery.data.length
      ? avatarsQuery.data
      : [];

  const botQuery = botQuerys.useBot(
    async (bId) => {
      const bot = await getBot(bId);
      const formattedBot = await botHelpers.formatBot(bot.Item, username);
      return formattedBot;
    },
    {
      botId,
    },
    { enabled: !!botId },
  );

  const bot =
    botQuery.isSuccess && botQuery.data && botQuery.data.botId
      ? botQuery.data
      : {};

  const { learnLang } = bot;

  // Mutations
  const updateBotMutation = botQuerys.useUpdateBot(updateBotAPI, {
    onSuccess: (data, variables) => {
      botHelpers.invalidateBotCache({
        botId: variables.botId,
        ...variables.bot,
        ...variables.values,
        ...data.Attributes,
      });
      queryClient.invalidateQueries({
        queryKey: botQuerys.botKeys.list({
          createdBy: username,
        }),
      });
      if (courseId && sectionId && topicId) {
        // invalidate for view topic page
        queryClient.invalidateQueries({
          queryKey: botQuerys.botKeys.list({
            courseSectionTopicId: `${courseId}|${sectionId}|${topicId}`,
            limit: config.limits.bots,
          }),
        });
      }
      openSnackbar("updated", "success");
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });

  const handleSpeak = async (params) => {
    try {
      const {
        text,
        learnLanguage,
        voiceName,
        mute,
        callback,
        textContainer,
        languageCode,
        pitch,
      } = params;
      const result = await tts(
        text,
        learnLanguage,
        voiceName,
        languageCode,
        null,
        pitch,
      );
      if (!mute && result && result.audioUrl) {
        const highlightCallback = () =>
          highlightSpeechText(
            textContainer,
            result.speechMarks,
            setTimeoutArray,
          );
        addToQueue(result.audioUrl, callback, highlightCallback);
      } else if (result.errorMessage) {
        throw new Error(result.errorMessage);
      }
      return result;
    } catch (error) {
      logger.error(error);
      return null;
    }
  };

  const handleStopSpeaking = async (textContainer) => {
    try {
      stopHighlightingSpeechText(textContainer, timeoutArray);
      stop();
      clearQueue();
    } catch (error) {
      logger.error(error);
    }
  };

  const searchParams = searchStringToObj(location.search);

  const addSearchParams = (params) => {
    let searchPp = objToSearchString({
      ...searchParams,
      ...params,
    });

    navigate({
      ...location,
      search: searchPp,
    });
  };

  const removeSearchParams = (keys = []) => {
    for (const key of keys) {
      key in searchParams && delete searchParams[key];
    }
    navigate({
      ...location,
      search: objToSearchString(searchParams),
    });
  };

  const handleUpdateBot = async (values, bag) => {
    try {
      if (values.visibility) {
        if (
          values.visibility &&
          values.visibility !== "public" &&
          values.visibility !== "private"
        ) {
          throw new Error();
        }
      }
      if (values.difficulty) {
        if (!difficulties.includes(values.difficulty)) {
          throw new Error();
        }
      }
      if (values.botId) {
        delete values.botId;
      }
      if (values.phrasesToBoost) {
        values.phrasesToBoost.filter((str) => str.replace(/\s/g, "").length);
      } else {
        values.phrasesToBoost = [];
      }

      if (values.picture) {
        values.pictureWarnDetails = {};
        const base64String = values.picture.split(",");
        const [picture, pictureS3Key, moderated, warn, moderatedLabels] =
          await UploadBotImage(base64String[1] || base64String[0], botId);
        if (moderated || warn) {
          if (verificationStatus === "approved" || warn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            values.pictureWarnDetails = {
              user: username,
              labels: moderatedLabels.join(", "),
            };
            if (confirmation !== true) {
              return;
            }
          } else {
            const moderationReason = moderatedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (picture) {
          values.picture = picture;
          values.pictureS3Key = pictureS3Key;
        } else {
          delete values.picture;
        }
      }
      if (values.botAvatar) {
        values.botAvatarWarnDetails = {};
        const base64String = values.botAvatar.split(",");
        const [
          botAvatar,
          botAvatarS3Key,
          botModerated,
          botWarn,
          botModeratedLabels,
        ] = await UploadBotImage(
          base64String[1] || base64String[0],
          `${botId}-botAvatar`,
        );
        if (botModerated || botWarn) {
          if (verificationStatus === "approved" || botWarn) {
            const confirmation = await confirm(
              `${t("warning")}`,
              `${t("moderation_warning")}${botModeratedLabels.join(", ")}`,
              `${t("use_anyways")}`,
            );
            values.botAvatarWarnDetails = {
              user: username,
              labels: botModeratedLabels.join(", "),
            };
            if (confirmation !== true) {
              return;
            }
          } else {
            const moderationReason = botModeratedLabels[0];
            handleOpenModerateDialog(moderationReason);
            return;
          }
        }
        if (botAvatar) {
          values.botAvatar = botAvatar;
          values.botAvatarS3Key = botAvatarS3Key;
        } else {
          delete values.botAvatar;
        }
      }
      updateBotMutation.mutate({ botId: bot.botId, values, bot });
      if (bag) {
        if (bag.resetForm) {
          bag.resetForm({ values });
        }
        bag.setSubmitting(false);
      }
    } catch (error) {
      logger.error(error);
      if (bag) {
        if (bag.resetForm) {
          bag.resetForm({ values });
        }
        bag.setSubmitting(false);
      }
    }
  };

  const handlePlayBot = async (bot) => {
    const { courseSectionTopicId, botId } = bot;
    const idValues = courseSectionTopicId
      ? courseSectionTopicId.split("|")
      : [];
    if (classroomId && courseId && sectionId && topicId) {
      navigate(
        `/classrooms/${classroomId}/${courseId}/${sectionId}/${topicId}/bots/${botId}`,
      );
    } else if (idValues && idValues.length === 3) {
      const courseId = idValues[0].trim();
      const sectionId = idValues[1].trim();
      const topicId = idValues[2].trim();
      navigate(`/courses/${courseId}/${sectionId}/${topicId}/bots/${botId}`);
    } else {
      navigate(`/bots/${botId}`);
    }
  };

  const handleEditBot = async (bot) => {
    const { courseSectionTopicId, botId } = bot;
    const idValues = courseSectionTopicId
      ? courseSectionTopicId.split("|")
      : [];
    if (idValues && idValues.length === 3) {
      const courseId = idValues[0].trim();
      const sectionId = idValues[1].trim();
      const topicId = idValues[2].trim();
      navigate(
        `/courses/${courseId}/${sectionId}/${topicId}/bots/${botId}/edit`,
      );
    } else {
      navigate(`/bots/${botId}/edit`);
    }
  };

  const handleUploadBotExerciseImage = async (botId, value) => {
    try {
      if (botId && value) {
        const imageId = uuidv4();
        const pictureBase64String = value.split(",");
        const [url] = await UploadBotExerciseImage(
          botId,
          pictureBase64String[1] || pictureBase64String[0],
          imageId,
        );
        return url;
      }
      throw new Error(t("missing_details"));
    } catch (error) {
      logger.error(error);
      openSnackbar(error.message, "error");
      return null;
    }
  };

  const handleBotInstructionsImageUpload = (file, onSuccess, onError) => {
    try {
      if (file) {
        if (file.size < config.fileSizeLimit) {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.addEventListener(
            "load",
            async () => {
              const imageId = uuidv4();
              const imageBase64String = reader.result.split(",");
              const [picture, , moderated, warn, moderatedLabels] =
                await UploadBotExerciseImage(
                  botId,
                  imageBase64String[1] || imageBase64String[0],
                  `instructions-${imageId}`,
                );
              if (moderated || warn) {
                if (verificationStatus === "approved" || warn) {
                  const confirmation = await confirm(
                    `${t("warning")}`,
                    `${t("moderation_warning")}${moderatedLabels.join(", ")}`,
                    `${t("use_anyways")}`,
                  );
                  if (confirmation !== true) {
                    return;
                  } else if (confirmation === true) {
                    onSuccess(picture);
                  }
                } else {
                  const moderationReason = moderatedLabels[0];
                  handleOpenModerateDialog(moderationReason);
                  return;
                }
              } else {
                onSuccess(picture);
              }
            },
            false,
          );
        } else {
          onError("File size exceeded");
        }
      }
    } catch (error) {
      logger.error(error);
      onError(error.message);
    }
  };

  const topicQuery = courseQuerys.useTopic(
      async (cId, sId, tId) => {
        const topic = await getCourseSectionTopic(cId, sId, tId);
        const formattedTopic = await courseHelpers.formatTopic(
          topic.Item,
          props.authData,
        );
        return formattedTopic;
      },
      {
        courseId,
        sectionId,
        topicId,
        username: props.authData.username,
      },
      { enabled: !!courseId && !!sectionId && !!topicId },
    );
  
    const courseSectionTopic =
      topicQuery.isSuccess && topicQuery.data && topicQuery.data.topicId
        ? topicQuery.data
        : {};
  
    const phraseListsQuery = courseQuerys.useCourseSectionTopicPhraseLists(
      undefined,
      { courseId, sectionId, topicId, filters: { includePhrases: "true" } },
      { enabled: !!courseId && !!sectionId && !!topicId },
    );
  
    const phraseLists =
      phraseListsQuery.isSuccess &&
      phraseListsQuery.data &&
      Array.isArray(phraseListsQuery.data.Items)
        ? phraseListsQuery.data.Items.map((pl) => ({
            id: pl.phraseListId,
            name: pl.phraseListName,
            phrases: Array.isArray(pl.phrases)
              ? [...pl.phrases].sort((a, b) => (a?.idx ?? 0) - (b?.idx ?? 0))
              : [],
          }))
        : [];
  
    const allTopicPhrases =
    Array.isArray(phraseLists) && phraseLists.length > 0
      ? phraseLists.flatMap((pl) => pl.phrases)
      : [];

  const currentBotTemplate =
    learnLang &&
    config.languages[learnLang].botBlueprints.find(
      (b) => b.botId === bot.selectedTemplate,
    );

  const learnLangCharacters = learnLang
    ? config.languages[learnLang.toLowerCase() || "english"].characters
    : [];
  const forLangCharacters = getForLangCharacters(forLang);
  let specialCharacters = learnLangCharacters.concat(forLangCharacters);
  specialCharacters = [...new Set(specialCharacters)];

  return (
    <div>
      {bot && bot.botId && (
        <BotEditor
          t={t}
          initialValues={bot}
          handleClose={handleCloseBotEditor}
          open={isBotEditorOpen}
          onSubmit={handleUpdateBot}
          learnLangCharacters={specialCharacters}
          forLangCharacters={specialCharacters}
          avatars={avatars}
          voices={
            learnLang
              ? config.languages[learnLang.toLowerCase() || "english"].Voices
              : []
          }
          isBotHidden={bot ? bot.isBotHidden : null}
          courseId={courseId}
          sectionId={sectionId}
          topicId={topicId}
          navigate={navigate}
          handleSpeak={handleSpeak}
          handleStopSpeaking={handleStopSpeaking}
          learnLang={learnLang}
          forLang={forLang}
          openSnackbar={openSnackbar}
          exerciseName="bot"
          handleTranslate={translate}
          handleMessageBot={messageBot}
          userAttributes={attributes}
          addSearchParams={addSearchParams}
          removeSearchParams={removeSearchParams}
          handlePlayBot={handlePlayBot}
          handleEditBot={handleEditBot}
          handleShareBot={handleShareBot}
          fileSizeLimit={config.fileSizeLimit}
          handleUploadBotExerciseImage={handleUploadBotExerciseImage}
          subscription={subscription}
          handleBotInstructionsImageUpload={handleBotInstructionsImageUpload}
          makeChatGptApiRequest={makeChatGptApiRequest}
          handleUpdateBot={handleUpdateBot}
          selectedTemplate={bot && bot.selectedTemplate}
          siteLanguage={i18n.language}
          topicPhrases = {
            allTopicPhrases.length > 0
                ? allTopicPhrases
                : (courseSectionTopic.phrases || [])
        }        
          phraseLists={phraseLists}
          initialTopicGoal={courseSectionTopic && courseSectionTopic.topicGoal ? courseSectionTopic.topicGoal : ""}
          isUserInternal={isUserInternal}
          verificationStatus={verificationStatus}
        />
      )}
    </div>
  );
}

EditBot.propTypes = {
  navigate: PropTypes.object,
  authData: PropTypes.object,
};

export default EditBot;
